import { Generation, MessageUnionType, RunSchema } from '@/types/schema';
import { getRunName } from '@/utils/get-run-name';
import { getHubInfoFromRun } from '@/utils/hub';
import { maybeGetArrayOfMessages } from '@/utils/messages';
import { localTime } from '@/utils/utc-time';

import { LANGCHAIN_DEFAULT_NAMES } from '../components/RunDetails.utils';

export const EMPTY_LIST = [];
export const CHILD_RUNS_SELECT = [
  'end_time',
  'status',
  'total_tokens',
  'completion_tokens',
  'prompt_tokens',
  'total_cost',
  'completion_cost',
  'prompt_cost',
  'tags',
  'name',
  'run_type',
  'parent_run_id',
  'dotted_order',
  'first_token_time',
  'extra',
  'inputs_s3_urls',
  'outputs_s3_urls',
];
export type Qualifier = (run: RunSchema, children: RunSchema[]) => boolean;
export type FlatRun = {
  run: RunSchema;
  depth: number;
  isActiveDescendant: boolean;
  rootRunId?: string;
  conversationIdx?: number;
};
export type RowDepthMeta = {
  [depth: number]: {
    right?: 'black' | 'red' | 'blue' | 'purple';
    bottom?: 'black' | 'red' | 'blue' | 'purple';
  };
};

export const qualifyFromHeuristics: Qualifier = (run) => {
  if (run.tags?.includes('langsmith:hidden')) {
    return false;
  }

  // retrievers, tools and llms are interesting
  if (
    run.run_type === 'retriever' ||
    run.run_type === 'tool' ||
    run.run_type === 'llm'
  ) {
    return true;
  }

  // nodes which are not our built-in names and are not lambdas are interesting
  const name = getRunName(run);
  const nameWithoutBrackets = name.match('(.+)<.*>')?.groups?.[0] ?? name;
  if (
    !nameWithoutBrackets.startsWith('Runnable') &&
    nameWithoutBrackets !== '<lambda>' &&
    !LANGCHAIN_DEFAULT_NAMES.has(nameWithoutBrackets)
  ) {
    return true;
  }

  // runs which failed or pending are interesting
  if (run.status === 'error' || run.status === 'pending') {
    return true;
  }

  // runs which are tagged as coming from hub are interesting
  if (getHubInfoFromRun(run)) {
    return true;
  }

  return false;
};

export function createCompactTree(params: {
  rootRuns: RunSchema[];
  activeRun?: RunSchema;
  childRunLookup: Record<string, RunSchema[] | undefined>;
  qualifier: Qualifier[];
}): [
  Record<string, RunSchema[] | undefined>,
  Record<string, RunSchema>,
  number
] {
  const rootRunIds = params.rootRuns.map((r) => r.id);
  const queue = params.rootRuns.map((rootRun) => ({
    node: rootRun,
    path: [] as string[],
    tags: rootRun.tags ?? [],
  }));

  const selectedRuns: Record<string, RunSchema> = {};

  // parent node should be already present in the selectedRuns map
  function addNode(node: RunSchema, path: string[]) {
    if (selectedRuns[node.id]) return;

    selectedRuns[node.id] = {
      ...node,
      parent_run_id: path[path.length - 1],
      parent_run_ids: path,
      child_run_ids: undefined,
      direct_child_run_ids: undefined,
    };

    for (const parentId of path) {
      if (selectedRuns[parentId]) {
        selectedRuns[parentId].child_run_ids ??= [];
        selectedRuns[parentId].child_run_ids?.push(node.id);

        if (parentId === selectedRuns[node.id].parent_run_id) {
          selectedRuns[parentId].direct_child_run_ids ??= [];
          selectedRuns[parentId].direct_child_run_ids?.push(node.id);
        }
      }
    }
  }

  const visited = new Set<string>();
  while (queue.length > 0) {
    const tail = queue.pop();
    if (!tail) break;

    if (visited.has(tail.node.id)) continue;
    visited.add(tail.node.id);

    const children = params.childRunLookup[tail.node.id] ?? [];

    // root run is always interesting
    if (rootRunIds.includes(tail.node.id)) {
      addNode(tail.node, tail.path);
    }

    // active run is always interesting
    if (params.activeRun && tail.node.id === params.activeRun.id) {
      addNode(tail.node, tail.path);
    }

    // if the qualifier is satisfied, add the node
    if (params.qualifier.some((qualifier) => qualifier(tail.node, children))) {
      addNode(tail.node, tail.path);
    }

    const path =
      tail.node.id in selectedRuns ? [...tail.path, tail.node.id] : tail.path;

    queue.push(
      ...children
        .map((node) => ({
          node,
          path,
          tags: tail.tags.concat(node.tags ?? []),
        }))
        .reverse()
    );
  }

  const childRunLookup = Object.fromEntries(
    Object.entries(selectedRuns).map(([id, run]) => [
      id,
      run.direct_child_run_ids?.map((id) => selectedRuns[id]).filter(Boolean),
    ])
  );

  return [childRunLookup, selectedRuns, visited.size] as const;
}

export function isUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    if (value.length < 3) return false;

    const url = new URL(value);

    // Check protocol
    if (!['http:', 'https:'].includes(url.protocol)) return false;

    // Check for valid domain
    if (!url.hostname.includes('.')) return false;

    return true;
  } catch (e) {
    return false;
  }
}

export function showChatMessageComponents(inputs: RunSchema['inputs']) {
  let inputMessages = maybeGetArrayOfMessages(inputs?.messages);

  // special case for open ai js input which divides up the input
  // into messages in the first argument and options in the second
  if (!inputMessages && inputs?.args) {
    inputMessages = maybeGetArrayOfMessages(inputs.args[0]?.messages);
  }

  return Boolean(inputMessages);
}

export function getOutputMessagesWithIndex(generations: Generation[]) {
  return generations
    ?.map((g, index) =>
      'message' in g
        ? ([g.message, index] as [MessageUnionType | undefined, number])
        : undefined
    )
    .filter((m) => m != null);
}

export function calculateMinMaxTimeRanges(
  flattenedRuns: Array<{ run: { start_time: string; end_time?: string } }>
) {
  const n = flattenedRuns.length;
  const result: Array<{ min: number; max: number }> = new Array(n);

  // Start from the last element
  let currentMin = Infinity;
  let currentMax = -Infinity;

  // Go backwards through the array
  for (let i = n - 1; i >= 0; i--) {
    const run = flattenedRuns[i].run;
    const startTime = localTime(run.start_time).valueOf();
    const endTime = run.end_time
      ? localTime(run.end_time).valueOf()
      : startTime;

    // Update running min/max
    currentMin = Math.min(currentMin, startTime);
    currentMax = Math.max(currentMax, endTime);

    // Store result for this position
    result[i] = {
      min: currentMin,
      max: currentMax,
    };
  }

  return result;
}

const graphInterruptRegex =
  /^GraphInterrupt\(.*Interrupt.*\)|^ParentCommand|^GraphDelegate|^GraphBubbleUp/ms;
export function isGraphInterrupt(error?: string): boolean {
  if (!error) return false;
  return graphInterruptRegex.test(error);
}
